import React, { useState, useEffect } from "react";
import {
  FaBook, FaUsers, FaCog, FaBrain, FaProjectDiagram,
  FaFileAlt, FaCertificate, FaArrowLeft, FaPlus, FaEdit, FaTrash
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import MonoButton from "../kits/DesignKit/MonoButton";
import PaperDarkBG from "../kits/DesignKit/PaperDarkBG";
import PaperBG from "../kits/DesignKit/PaperBG";
import BookingModal from "./BookingModal";

// Demo-Daten für Admin-Dashboard
const DEMO_COURSES = [
  {
    id: 1,
    title: "React Framework",
    category: "nodejs",
    modules: 12,
    enrolled: 45,
    status: "Aktiv",
  },
  {
    id: 2,
    title: "After Effects SocialMedia Lottie",
    category: "code",
    modules: 8,
    enrolled: 23,
    status: "Aktiv",
  },
  {
    id: 3,
    title: "Cinema4D für Produkte und Werbung",
    category: "3d",
    modules: 15,
    enrolled: 34,
    status: "Aktiv",
  },
];

const DEMO_USERS = [
  { id: 1, name: "Denis", surname: "Bitter", email: "saghallo@denisbitter.de", courses: 2, status: "Aktiv" },
  { id: 999, name: "Denis", surname: "USERDEMO", email: "demo@denisbitter.de", courses: 1, status: "Aktiv" },
];

const DEMO_SKILLS = [
  { id: 1, title: "Photoshop", problem: "Kann ich JSX in Photoshop nutzen?", category: "Media" },
  { id: 2, title: "Animation", problem: "Ich will meine Animationen in den Browser bringen", category: "Code" },
  { id: 3, title: "Mac OSX Ordner", problem: "MacOS erlaubt mir keine anderen Ordner", category: "System" },
];

const DEMO_PROJECTS = [
  { id: 3, title: "Social Media Content erstellen mit Adobe AfterEffects", status: "Aktiv" },
  { id: 4, title: "Produkt Visualisierung", status: "Aktiv" },
];

const DEMO_PROFESSIONS = [
  {
    title: "Photoshop",
    problem: "Kann ich JSX in Photoshop nutzen?",
    challenge: "Ich möchte Templates für wiederkehrende Aufgaben haben.",
    solution: "Na klar – und das für alles. Zum Beispiel: LinkedIn & YouTube-Banner einfach per JSX-Vorlage."
  },
  {
    title: "Illustrator Datei in AfterEffects",
    problem: "Illustrator in After Effects – ohne Pfadänderung",
    challenge: "Jedes Mal muss ich die Pfade manuell neu verknüpfen.",
    solution: "Du kannst AI-Dateien direkt mit referenzierten Pfaden in AE importieren – ohne Umwege."
  },
];

function LMSDemo() {
  const [activeTab, setActiveTab] = useState("courses"); // courses, users, skills, projects, professions
  const [showBooking, setShowBooking] = useState(false);
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [showProfessionForm, setShowProfessionForm] = useState(false);
  const [showCertificatePreview, setShowCertificatePreview] = useState(null); // "standard" or "teilnahme"
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const navigate = useNavigate();

  // Scroll to center modal in viewport when booking modal opens
  useEffect(() => {
    if (showBooking) {
      const viewportHeight = window.innerHeight;
      const scrollTop = window.scrollY;
      const targetScroll = Math.max(0, scrollTop - (viewportHeight / 4));
      window.scrollTo({ top: targetScroll, behavior: 'smooth' });
    }
  }, [showBooking]);

  return (
    <PaperBG>
      <div className="max-w-7xl mx-auto px-4 py-8 font-mono">
        {/* Back Button */}
        <div className="mb-4">
          <MonoButton
            onClick={() => navigate("/")}
            className="text-xs px-3 py-1 flex items-center gap-2"
          >
            <FaArrowLeft /> Zurück zur Startseite
          </MonoButton>
        </div>

        {/* Header */}
        <PaperDarkBG className="rounded-lg mb-6">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FaCog className="text-brand text-xl" />
                <div>
                  <h1 className="text-sm font-semibold text-shadow">LMS Admin Dashboard Demo</h1>
                  <p className="text-xs text-gray-300">Komplette Kursverwaltung & Content-Management</p>
                </div>
              </div>
            </div>
          </div>
        </PaperDarkBG>

        {/* Info Banner */}
        <div className="bg-gradient-to-r from-orange-50 to-orange-100 border-l-4 border-brand px-4 py-3 mb-6 rounded">
          <p className="text-xs text-gray-800">
            <strong>Admin-Demo:</strong> Dies zeigt die <strong>Admin-Perspektive</strong> unseres LMS-Systems.
            Kurse erstellen, Skills verwalten, Projekte zuordnen, Profession Data bearbeiten – alles in einem System.{" "}
            <button
              onClick={() => setShowBooking(true)}
              className="text-brand underline hover:no-underline font-semibold cursor-pointer"
            >
              Kontaktiere uns für dein eigenes LMS
            </button>.
          </p>
        </div>

        {/* Admin Navigation Tabs */}
        <div className="bg-white rounded-lg border border-gray-300 mb-6">
          <div className="flex overflow-x-auto border-b border-gray-300">
            <button
              onClick={() => setActiveTab("courses")}
              className={`flex items-center gap-2 px-4 py-3 text-xs font-semibold whitespace-nowrap transition-colors ${
                activeTab === "courses"
                  ? "border-b-2 border-brand text-brand bg-orange-50"
                  : "text-gray-600 hover:text-brand hover:bg-gray-50"
              }`}
            >
              <FaBook /> Kursverwaltung
            </button>
            <button
              onClick={() => setActiveTab("users")}
              className={`flex items-center gap-2 px-4 py-3 text-xs font-semibold whitespace-nowrap transition-colors ${
                activeTab === "users"
                  ? "border-b-2 border-brand text-brand bg-orange-50"
                  : "text-gray-600 hover:text-brand hover:bg-gray-50"
              }`}
            >
              <FaUsers /> Benutzerverwaltung
            </button>
            <button
              onClick={() => setActiveTab("skills")}
              className={`flex items-center gap-2 px-4 py-3 text-xs font-semibold whitespace-nowrap transition-colors ${
                activeTab === "skills"
                  ? "border-b-2 border-brand text-brand bg-orange-50"
                  : "text-gray-600 hover:text-brand hover:bg-gray-50"
              }`}
            >
              <FaBrain /> Skill Manager
            </button>
            <button
              onClick={() => setActiveTab("projects")}
              className={`flex items-center gap-2 px-4 py-3 text-xs font-semibold whitespace-nowrap transition-colors ${
                activeTab === "projects"
                  ? "border-b-2 border-brand text-brand bg-orange-50"
                  : "text-gray-600 hover:text-brand hover:bg-gray-50"
              }`}
            >
              <FaProjectDiagram /> Project Manager
            </button>
            <button
              onClick={() => setActiveTab("professions")}
              className={`flex items-center gap-2 px-4 py-3 text-xs font-semibold whitespace-nowrap transition-colors ${
                activeTab === "professions"
                  ? "border-b-2 border-brand text-brand bg-orange-50"
                  : "text-gray-600 hover:text-brand hover:bg-gray-50"
              }`}
            >
              <FaFileAlt /> Profession Data
            </button>
            <button
              onClick={() => setActiveTab("certificates")}
              className={`flex items-center gap-2 px-4 py-3 text-xs font-semibold whitespace-nowrap transition-colors ${
                activeTab === "certificates"
                  ? "border-b-2 border-brand text-brand bg-orange-50"
                  : "text-gray-600 hover:text-brand hover:bg-gray-50"
              }`}
            >
              <FaCertificate /> Zertifikate & PDFs
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg border border-gray-300 p-6">

          {/* Kursverwaltung Tab */}
          {activeTab === "courses" && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-sm font-bold">Kursverwaltung</h2>
                <MonoButton
                  onClick={() => setShowCourseForm(!showCourseForm)}
                  className="text-xs px-3 py-2 flex items-center gap-2"
                >
                  <FaPlus /> Kurs erstellen
                </MonoButton>
              </div>

              {showCourseForm && (
                <div className="bg-gray-50 border border-gray-300 rounded-lg p-6 mb-6">
                  <h3 className="text-xs font-bold mb-4">Neuen Kurs anlegen</h3>
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="Projekt Trainings Name (z. B. 3D, Web, Video)"
                      className="w-full px-3 py-2 text-xs border border-gray-300 rounded"
                    />
                    <select className="w-full px-3 py-2 text-xs border border-gray-300 rounded">
                      <option value="">Kategorie wählen</option>
                      <option value="0">WebDev</option>
                      <option value="3d">3D</option>
                      <option value="code">Code</option>
                      <option value="nodejs">NodeJS</option>
                    </select>
                    <textarea
                      placeholder="Was könnte mit dem Training für den Kunden für Vorteil sein"
                      rows="3"
                      className="w-full px-3 py-2 text-xs border border-gray-300 rounded"
                    />
                    <input
                      type="text"
                      placeholder="Titel"
                      className="w-full px-3 py-2 text-xs border border-gray-300 rounded"
                    />
                    <textarea
                      placeholder="Vor welchem Problem stand der Kunde"
                      rows="3"
                      className="w-full px-3 py-2 text-xs border border-gray-300 rounded"
                    />
                    <textarea
                      placeholder="Welches Training wurde absolviert – Modul X in Y"
                      rows="3"
                      className="w-full px-3 py-2 text-xs border border-gray-300 rounded"
                    />
                    <div className="flex gap-2">
                      <MonoButton className="text-xs px-4 py-2">
                        Kurs speichern
                      </MonoButton>
                      <MonoButton
                        onClick={() => setShowCourseForm(false)}
                        className="text-xs px-4 py-2 bg-gray-500"
                      >
                        Abbrechen
                      </MonoButton>
                    </div>
                  </div>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-black text-white">
                    <tr>
                      <th className="px-4 py-3 text-left">Titel</th>
                      <th className="px-4 py-3 text-left">Kategorie</th>
                      <th className="px-4 py-3 text-left">Module</th>
                      <th className="px-4 py-3 text-left">Teilnehmer</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DEMO_COURSES.map((course) => (
                      <tr key={course.id} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3">{course.title}</td>
                        <td className="px-4 py-3">
                          <span className="bg-gray-900 text-white px-2 py-1 rounded text-xs">
                            {course.category}
                          </span>
                        </td>
                        <td className="px-4 py-3">{course.modules}</td>
                        <td className="px-4 py-3">{course.enrolled}</td>
                        <td className="px-4 py-3">
                          <span className="text-green-700 font-semibold">{course.status}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button className="text-blue-600 hover:text-blue-800">
                              <FaEdit />
                            </button>
                            <button className="text-red-600 hover:text-red-800">
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Benutzerverwaltung Tab */}
          {activeTab === "users" && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-sm font-bold">Benutzerverwaltung</h2>
                <MonoButton className="text-xs px-3 py-2 flex items-center gap-2">
                  <FaPlus /> Benutzer hinzufügen
                </MonoButton>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-black text-white">
                    <tr>
                      <th className="px-4 py-3 text-left">ID</th>
                      <th className="px-4 py-3 text-left">Name</th>
                      <th className="px-4 py-3 text-left">Vorname</th>
                      <th className="px-4 py-3 text-left">E-Mail</th>
                      <th className="px-4 py-3 text-left">Kurse</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DEMO_USERS.map((user) => (
                      <tr key={user.id} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3">{user.id}</td>
                        <td className="px-4 py-3">{user.surname}</td>
                        <td className="px-4 py-3">{user.name}</td>
                        <td className="px-4 py-3">{user.email}</td>
                        <td className="px-4 py-3">{user.courses}</td>
                        <td className="px-4 py-3">
                          <span className="text-green-700 font-semibold">{user.status}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button className="text-blue-600 hover:text-blue-800">
                              <FaEdit />
                            </button>
                            <button className="text-red-600 hover:text-red-800">
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Skill Manager Tab */}
          {activeTab === "skills" && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-sm font-bold">Skill Verwaltung</h2>
                <MonoButton className="text-xs px-3 py-2 flex items-center gap-2">
                  <FaPlus /> Skill erstellen
                </MonoButton>
              </div>

              <div className="space-y-4">
                {DEMO_SKILLS.map((skill) => (
                  <div key={skill.id} className="border border-gray-300 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-bold text-sm mb-1">{skill.title}</h3>
                        <span className="text-xs bg-gray-900 text-white px-2 py-1 rounded">
                          {skill.category}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button className="text-blue-600 hover:text-blue-800">
                          <FaEdit />
                        </button>
                        <button className="text-red-600 hover:text-red-800">
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-700 mb-2">
                      <strong>Problem:</strong> {skill.problem}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Project Manager Tab */}
          {activeTab === "projects" && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-sm font-bold">Projekt-Verwaltung</h2>
                <MonoButton className="text-xs px-3 py-2 flex items-center gap-2">
                  <FaPlus /> Neues Projekt
                </MonoButton>
              </div>

              <div className="space-y-4">
                {DEMO_PROJECTS.map((project) => (
                  <div key={project.id} className="border border-gray-300 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-bold text-sm mb-2">{project.title}</h3>
                        <span className="text-xs text-green-700 font-semibold">{project.status}</span>
                      </div>
                      <div className="flex gap-2">
                        <button className="text-blue-600 hover:text-blue-800">
                          <FaEdit />
                        </button>
                        <button className="text-red-600 hover:text-red-800">
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Profession Data Tab */}
          {activeTab === "professions" && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-sm font-bold">Profession Data – Smart Editor (Lokal)</h2>
                <div className="flex gap-2">
                  <MonoButton
                    onClick={() => setShowProfessionForm(!showProfessionForm)}
                    className="text-xs px-3 py-2 flex items-center gap-2"
                  >
                    <FaPlus /> Neu
                  </MonoButton>
                  <MonoButton className="text-xs px-3 py-2">Export</MonoButton>
                  <MonoButton className="text-xs px-3 py-2">Import</MonoButton>
                </div>
              </div>

              {showProfessionForm && (
                <div className="bg-gray-50 border border-gray-300 rounded-lg p-6 mb-6">
                  <h3 className="text-xs font-bold mb-4">Eintrag bearbeiten</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold mb-1">TITEL</label>
                      <input
                        type="text"
                        placeholder="Photoshop"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-1">PROBLEM</label>
                      <textarea
                        placeholder="Kann ich JSX in Photoshop nutzen?"
                        rows="2"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-1">CHALLENGE</label>
                      <textarea
                        placeholder="Ich möchte Templates für wiederkehrende Aufgaben haben."
                        rows="2"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-1">LÖSUNG</label>
                      <textarea
                        placeholder="Na klar – und das für alles. Zum Beispiel: LinkedIn & YouTube-Banner einfach per JSX-Vorlage."
                        rows="3"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded"
                      />
                    </div>
                    <div className="flex gap-2">
                      <MonoButton className="text-xs px-4 py-2">Speichern</MonoButton>
                      <MonoButton
                        onClick={() => setShowProfessionForm(false)}
                        className="text-xs px-4 py-2 bg-gray-500"
                      >
                        Abbrechen
                      </MonoButton>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {DEMO_PROFESSIONS.map((prof, idx) => (
                  <div key={idx} className="border border-gray-300 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-bold text-sm">{prof.title}</h3>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setShowProfessionForm(true)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <FaEdit />
                        </button>
                        <button className="text-red-600 hover:text-red-800">
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2 text-xs">
                      <p><strong>Problem:</strong> {prof.problem}</p>
                      <p><strong>Challenge:</strong> {prof.challenge}</p>
                      <p><strong>Lösung:</strong> {prof.solution}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Zertifikate & PDFs Tab */}
          {activeTab === "certificates" && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-sm font-bold">Zertifikate & PDF-Verwaltung</h2>
                <MonoButton className="text-xs px-3 py-2 flex items-center gap-2">
                  <FaPlus /> Zertifikat erstellen
                </MonoButton>
              </div>

              <div className="space-y-6">
                {/* Feature Showcase */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border border-gray-300 rounded-lg p-4 bg-gradient-to-br from-orange-50 to-white">
                    <FaCertificate className="text-brand text-2xl mb-3" />
                    <h3 className="font-bold text-sm mb-2">Digitale Unterschrift</h3>
                    <p className="text-xs text-gray-700 mb-3">
                      Automatische Zertifikat-Generierung mit digitaler Unterschrift nach Kursabschluss.
                    </p>
                    <MonoButton className="text-xs px-3 py-2">Konfigurieren</MonoButton>
                  </div>

                  <div className="border border-gray-300 rounded-lg p-4 bg-gradient-to-br from-orange-50 to-white">
                    <FaFileAlt className="text-brand text-2xl mb-3" />
                    <h3 className="font-bold text-sm mb-2">PDF-Generator</h3>
                    <p className="text-xs text-gray-700 mb-3">
                      Automatische PDF-Erstellung für Kursmaterialien, Teilnehmerlisten und Berichte.
                    </p>
                    <MonoButton
                      onClick={() => setShowTemplateEditor(true)}
                      className="text-xs px-3 py-2"
                    >
                      Template bearbeiten
                    </MonoButton>
                  </div>
                </div>

                {/* Certificate Templates */}
                <div>
                  <h3 className="font-bold text-sm mb-3">Zertifikat-Vorlagen</h3>
                  <div className="space-y-3">
                    <div className="border border-gray-300 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-sm">Standard Zertifikat</h4>
                          <p className="text-xs text-gray-600">Format: A4, PDF mit digitaler Signatur</p>
                        </div>
                        <div className="flex gap-2">
                          <MonoButton
                            onClick={() => setShowCertificatePreview("standard")}
                            className="text-xs px-3 py-1"
                          >
                            Vorschau
                          </MonoButton>
                          <button className="text-blue-600 hover:text-blue-800">
                            <FaEdit />
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="border border-gray-300 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-sm">Teilnahmebestätigung</h4>
                          <p className="text-xs text-gray-600">Format: A4, PDF ohne Noten</p>
                        </div>
                        <div className="flex gap-2">
                          <MonoButton
                            onClick={() => setShowCertificatePreview("teilnahme")}
                            className="text-xs px-3 py-1"
                          >
                            Vorschau
                          </MonoButton>
                          <button className="text-blue-600 hover:text-blue-800">
                            <FaEdit />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Feature Highlights */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-gray-300 rounded-lg p-4">
            <div className="text-brand text-2xl mb-2">🎯</div>
            <h3 className="font-bold text-sm mb-2">Komplette Kursverwaltung</h3>
            <p className="text-xs text-gray-700">
              Kurse anlegen, Module erstellen, Inhalte verwalten – alles zentral an einem Ort.
            </p>
          </div>

          <div className="bg-white border border-gray-300 rounded-lg p-4">
            <div className="text-brand text-2xl mb-2">👥</div>
            <h3 className="font-bold text-sm mb-2">User & Team Management</h3>
            <p className="text-xs text-gray-700">
              Teilnehmer verwalten, Rollen zuweisen, Fortschritte tracken – individuell oder als Team.
            </p>
          </div>

          <div className="bg-white border border-gray-300 rounded-lg p-4">
            <div className="text-brand text-2xl mb-2">📊</div>
            <h3 className="font-bold text-sm mb-2">Analytics & Reporting</h3>
            <p className="text-xs text-gray-700">
              Detaillierte Statistiken, automatische Reports und Export-Funktionen für Weiterbildungsträger.
            </p>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="mt-8 bg-gray-900 text-white rounded-lg px-6 py-8 text-center">
          <h3 className="text-sm font-bold mb-2">Brauchst du ein eigenes LMS mit Admin-Dashboard?</h3>
          <p className="text-xs mb-4 text-gray-300">
            Mit Kursverwaltung, Skill Manager, Project Manager, Profession Data Editor, User-Management,
            PDF-Generator, digitaler Unterschrift und Zertifikaten – alles in einem System.
          </p>
          <MonoButton onClick={() => setShowBooking(true)} className="text-xs px-6 py-2">
            Kontaktiere uns für eine Demo
          </MonoButton>
        </div>

        {/* PDF Template Editor Modal */}
        {showTemplateEditor && (
          <div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4 py-8"
            onClick={() => setShowTemplateEditor(false)}
          >
            <div
              className="bg-white rounded-lg max-w-5xl w-full p-6 font-mono overflow-y-auto max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-sm font-bold">PDF Template Editor</h2>
                <button
                  onClick={() => setShowTemplateEditor(false)}
                  className="text-gray-500 hover:text-gray-700 text-xl"
                >
                  ×
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Template Editor */}
                <div>
                  <h3 className="text-xs font-bold mb-3 text-gray-700">Template-Einstellungen</h3>

                  <div className="space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-300">
                    <div>
                      <label className="block text-xs font-semibold mb-1">Header Logo URL</label>
                      <input
                        type="text"
                        placeholder="https://example.com/logo.png"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded"
                        defaultValue="https://lerneinfach.de/logo.png"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold mb-1">Firmenname</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded"
                        defaultValue="lerneinfach.de"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold mb-1">Zertifikat-Titel</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded"
                        defaultValue="Zertifikat"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold mb-1">Beschreibungstext</label>
                      <textarea
                        rows="3"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded"
                        defaultValue="Hiermit wird bestätigt, dass {NAME} den Kurs {KURS} erfolgreich abgeschlossen hat."
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Platzhalter: {"{NAME}"}, {"{KURS}"}, {"{DATUM}"}, {"{NOTE}"}
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold mb-1">Ort</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded"
                        defaultValue="Münster"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold mb-1">Unterschrift 1 - Name</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded"
                        defaultValue="Denis Bitter"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold mb-1">Unterschrift 1 - Titel</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded"
                        defaultValue="Instructor"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold mb-1">Unterschrift 2 - Name</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded"
                        defaultValue="lerneinfach.de"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold mb-1">Unterschrift 2 - Titel</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded"
                        defaultValue="Verwaltung"
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="showDigitalSignature"
                        defaultChecked
                        className="w-4 h-4"
                      />
                      <label htmlFor="showDigitalSignature" className="text-xs">
                        "✓ digital signiert" anzeigen
                      </label>
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="showCertificateId"
                        defaultChecked
                        className="w-4 h-4"
                      />
                      <label htmlFor="showCertificateId" className="text-xs">
                        Zertifikat-ID anzeigen
                      </label>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold mb-1">Border-Farbe (Hex)</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                          defaultValue="#FF7F50"
                        />
                        <input
                          type="text"
                          className="flex-1 px-3 py-2 text-xs border border-gray-300 rounded"
                          defaultValue="#FF7F50"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Live Preview */}
                <div>
                  <h3 className="text-xs font-bold mb-3 text-gray-700">Live Vorschau</h3>

                  <div className="bg-gradient-to-br from-orange-50 to-white border-4 border-brand p-8 rounded-lg">
                    <div className="text-center space-y-4">
                      <div className="w-12 h-12 bg-brand rounded-full flex items-center justify-center mx-auto">
                        <FaCertificate className="text-white text-xl" />
                      </div>
                      <h3 className="text-xs text-gray-600 uppercase tracking-wider">lerneinfach.de</h3>
                      <h1 className="text-xl font-bold text-gray-900">Zertifikat</h1>

                      <div className="space-y-2 text-xs text-gray-700">
                        <p>Hiermit wird bestätigt, dass</p>
                        <p className="text-lg font-bold text-gray-900 border-b-2 border-brand pb-1">Max Mustermann</p>
                        <p>den Kurs</p>
                        <p className="font-semibold text-gray-900">React Framework</p>
                        <p>erfolgreich abgeschlossen hat.</p>
                      </div>

                      <p className="text-xs text-gray-600 pt-4">
                        Münster, den {new Date().toLocaleDateString('de-DE')}
                      </p>

                      <div className="grid grid-cols-2 gap-4 pt-4">
                        <div className="border-t-2 border-gray-300 pt-2">
                          <p className="text-xs text-gray-600">Denis Bitter</p>
                          <p className="text-xs text-gray-500">Instructor</p>
                        </div>
                        <div className="border-t-2 border-gray-300 pt-2 relative">
                          <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 text-brand text-xs">
                            ✓ digital signiert
                          </div>
                          <p className="text-xs text-gray-600">lerneinfach.de</p>
                          <p className="text-xs text-gray-500">Verwaltung</p>
                        </div>
                      </div>

                      <p className="text-xs text-gray-400 pt-2">Zertifikat-ID: LMS-2026-DEMO</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex gap-2 justify-center">
                <MonoButton className="text-xs px-4 py-2">
                  Template speichern
                </MonoButton>
                <MonoButton className="text-xs px-4 py-2">
                  Vorschau als PDF
                </MonoButton>
                <MonoButton
                  onClick={() => setShowTemplateEditor(false)}
                  className="text-xs px-4 py-2 bg-gray-500"
                >
                  Schließen
                </MonoButton>
              </div>
            </div>
          </div>
        )}

        {/* Certificate Preview Modal */}
        {showCertificatePreview && (
          <div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4 py-8"
            onClick={() => setShowCertificatePreview(null)}
          >
            <div
              className="bg-white rounded-lg max-w-4xl w-full p-8 font-mono overflow-y-auto max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Certificate Preview Content */}
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-sm font-bold">
                  {showCertificatePreview === "standard" ? "Standard Zertifikat" : "Teilnahmebestätigung"}
                </h2>
                <button
                  onClick={() => setShowCertificatePreview(null)}
                  className="text-gray-500 hover:text-gray-700 text-xl"
                >
                  ×
                </button>
              </div>

              {/* Mock Certificate A4 Design */}
              <div className="bg-gradient-to-br from-orange-50 to-white border-4 border-brand p-12 aspect-[1/1.414] flex flex-col items-center justify-center text-center">
                {/* Logo/Header Area */}
                <div className="mb-8">
                  <div className="w-16 h-16 bg-brand rounded-full flex items-center justify-center mx-auto mb-4">
                    <FaCertificate className="text-white text-2xl" />
                  </div>
                  <h3 className="text-xs text-gray-600 uppercase tracking-wider">lerneinfach.de</h3>
                </div>

                {/* Certificate Title */}
                <h1 className="text-2xl font-bold mb-8 text-gray-900">
                  {showCertificatePreview === "standard" ? "Zertifikat" : "Teilnahmebestätigung"}
                </h1>

                {/* Certificate Body */}
                <div className="mb-8 space-y-4 max-w-md">
                  <p className="text-sm text-gray-700">
                    Hiermit wird bestätigt, dass
                  </p>
                  <p className="text-xl font-bold text-gray-900 border-b-2 border-brand pb-2">
                    Max Mustermann
                  </p>
                  <p className="text-sm text-gray-700">
                    den Kurs
                  </p>
                  <p className="text-lg font-semibold text-gray-900">
                    React Framework
                  </p>
                  <p className="text-sm text-gray-700">
                    erfolgreich abgeschlossen hat.
                  </p>

                  {showCertificatePreview === "standard" && (
                    <div className="pt-4">
                      <p className="text-xs text-gray-600 mb-2">Bewertung</p>
                      <p className="text-lg font-bold text-brand">Sehr gut (1,2)</p>
                    </div>
                  )}
                </div>

                {/* Date and Location */}
                <div className="mb-8 text-xs text-gray-600">
                  <p>Münster, den {new Date().toLocaleDateString('de-DE')}</p>
                </div>

                {/* Signature Area */}
                <div className="grid grid-cols-2 gap-8 w-full max-w-md">
                  <div className="text-center">
                    <div className="border-t-2 border-gray-300 pt-2">
                      <p className="text-xs text-gray-600">Denis Bitter</p>
                      <p className="text-xs text-gray-500">Instructor</p>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="border-t-2 border-gray-300 pt-2 relative">
                      <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-brand text-xs">
                        ✓ digital signiert
                      </div>
                      <p className="text-xs text-gray-600">lerneinfach.de</p>
                      <p className="text-xs text-gray-500">Verwaltung</p>
                    </div>
                  </div>
                </div>

                {/* Certificate ID */}
                <div className="mt-8 text-xs text-gray-400">
                  <p>Zertifikat-ID: LMS-2026-{Math.floor(Math.random() * 10000)}</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex gap-2 justify-center">
                <MonoButton className="text-xs px-4 py-2">
                  Als PDF exportieren
                </MonoButton>
                <MonoButton
                  onClick={() => setShowCertificatePreview(null)}
                  className="text-xs px-4 py-2 bg-gray-500"
                >
                  Schließen
                </MonoButton>
              </div>
            </div>
          </div>
        )}

        {/* Booking Modal */}
        {showBooking && <BookingModal onClose={() => setShowBooking(false)} />}
      </div>
    </PaperBG>
  );
}

export default LMSDemo;
